import { useLocale } from '@/components/Link'
import { localeDate } from '@/components/page-specific/gm/helpers/date'
import {
  type MenuVendorLocationWithDeliveryRegionsFragment,
  ServiceType,
  useGetVendorsInRangeLocationsLazyQuery,
  type UserBasketSettingsLocationFragment,
} from '@/generated/graphql'
import { getCountryCodeFromName } from '@/helpers/multiRegion/countries'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'
import { mobileMediaQuery } from '@caterdesk/ui--theme'
import { basketLocationToAddress } from '@caterdesk/utils--gm-validation'
import {
  AccessTime,
  CalendarToday,
  Close,
  Edit,
  InfoRounded,
  LocationOn,
  Person,
} from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  Tooltip as MuiTooltip,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { setDay } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMedia } from 'react-use'
import {
  type UserBasketSettings,
  validateUserBasketSettings,
  type ValidUserBasketSettings,
} from '../../domain/user-basket-settings'
import { useAddressLabel } from '../../helpers/useAddressLabel'
import useAllergenInfoText from '../../helpers/useAllergenInfoText'
import { useDeliveryTimeLabel } from '../../helpers/useDeliveryTimeLabel'
import { getCutOffLocations, validateAll } from '../../helpers/validation'
import { useAuthState } from '../states/auth'
import AddressLookup from './address-lookup'
import DatepickerInput from './datepicker-input'
import HeadCountInput from './headcount-input'
import { ButtonWrapper } from './styles'
import TimeSelect from './time-select'
import type { AppErrors } from '../../types/AppErrors'

type InputBarProps = {
  vendorId?: string
  modalOpen: boolean
  orderSelectedSettings: {
    date?: number
    time?: string
    headCount?: number
    location?: UserBasketSettingsLocationFragment
    weekdays?: Array<number>
  }
  hideFields?: {
    date?: boolean
  }
  disabled: boolean
  modalIsLoading: boolean
  availableVendorLocations?: MenuVendorLocationWithDeliveryRegionsFragment[]
  showBasketEmbeddedVersion?: boolean
  loading?: boolean
  onValueChange: (values: UserBasketSettings) => void
  setModalOpen: (open: boolean) => void
  onConfirm: (settings: ValidUserBasketSettings) => void
}

const InputBar = ({
  vendorId,
  orderSelectedSettings,
  modalOpen,
  modalIsLoading,
  availableVendorLocations: availableVendorLocationsProp,
  disabled,
  hideFields,
  showBasketEmbeddedVersion,
  loading,
  onValueChange,
  setModalOpen,
  onConfirm,
}: InputBarProps) => {
  const tenant = useTenant()
  const { t } = useTranslation()
  const allergenInfoText = useAllergenInfoText(t)
  const locale = useLocale()

  const isMobileScreen = useMedia(mobileMediaQuery, false)
  const [closeCalendar, setCloseCalender] = useState(false)

  const authState = useAuthState()

  const [location, setLocation] = useState<UserBasketSettingsLocationFragment | undefined>(
    orderSelectedSettings.location,
  )
  const { addressLabel: pinAddress, showFullAddress } = useAddressLabel(location)
  const [date, setDate] = useState<number | undefined>(orderSelectedSettings.date)
  const [time, setTime] = useState<string | undefined>(orderSelectedSettings.time)
  const [headcount, setHeadcount] = useState<number | undefined>(orderSelectedSettings.headCount)
  const [
    getVendorsInRangeLocations,
    { data: inRangeLocationData, loading: loadingInRangeLocationData },
  ] = useGetVendorsInRangeLocationsLazyQuery()
  const inRangeVendorLocations = useMemo(
    () => inRangeLocationData?.vendor.inRangeLocations ?? availableVendorLocationsProp ?? [],
    [availableVendorLocationsProp, inRangeLocationData?.vendor.inRangeLocations],
  )

  const [errors, setErrors] = useState<AppErrors>({})

  const deliveryTimeLabel = useDeliveryTimeLabel(time) || ''

  const cutOff5pm = useFeatureFlag(SPLITS.GM_CUTOFF_5PM)
  const availableVendorLocations = useMemo(
    () => getCutOffLocations(inRangeVendorLocations, cutOff5pm),
    [inRangeVendorLocations, cutOff5pm],
  )

  const newUserState = useMemo(
    () => ({
      ...orderSelectedSettings,
      date,
      location,
      headCount: headcount,
      time,
    }),
    [orderSelectedSettings, date, location, headcount, time],
  )
  const basketValidationResult = validateUserBasketSettings(newUserState)

  useEffect(() => {
    const { newErrors } = validateAll({
      inRangeVendorLocations: availableVendorLocations,
      userState: newUserState,
      useDeliveryTimes: true,
      deliveryTimes: tenant.features?.gmDeliveryTimeslots,
      t,
    })
    setErrors(newErrors)
  }, [availableVendorLocations, newUserState, t, tenant.features?.gmDeliveryTimeslots])

  const orderCanBeFulfilled =
    Object.keys(errors).length === 0 && basketValidationResult.type === 'success'

  useEffect(() => {
    setLocation(orderSelectedSettings.location)
    setDate(orderSelectedSettings.date)
    setTime(orderSelectedSettings.time)
    setHeadcount(orderSelectedSettings.headCount)
  }, [orderSelectedSettings])

  useEffect(() => setCloseCalender(false), [closeCalendar])

  const checkInRangeLocations = useCallback(
    async (locationToCheck: UserBasketSettingsLocationFragment) => {
      if (!vendorId) return
      const { latitude, longitude, country } = basketLocationToAddress(locationToCheck)
      const countryCode = getCountryCodeFromName(country) || null
      if (!latitude || !longitude) return
      return getVendorsInRangeLocations({
        variables: {
          vendorId,
          lat: latitude,
          lng: longitude,
          serviceType: ServiceType.Gm,
          countryCode,
          dateTime: null,
        },
      })
    },
    [getVendorsInRangeLocations, vendorId],
  )

  useEffect(() => {
    if (!orderSelectedSettings.location) return

    checkInRangeLocations(orderSelectedSettings.location)
  }, [orderSelectedSettings.location, checkInRangeLocations])

  const handleAddressChange = async (newLocation: UserBasketSettingsLocationFragment) => {
    setLocation(newLocation)

    const result = await checkInRangeLocations(newLocation)

    if (result?.data && result.data.vendor.inRangeLocations.length > 0) {
      setErrors({ ...errors, address: '' })
      onValueChange({ location: newLocation })
    }
  }

  const handleDateChange = (newDate: number) => {
    setDate(newDate)

    const { newErrors } = validateAll({
      inRangeVendorLocations: availableVendorLocations,
      userState: { ...newUserState, date: newDate },
      useDeliveryTimes: cutOff5pm,
      deliveryTimes: tenant.features?.gmDeliveryTimeslots,
      t,
    })

    setErrors(newErrors)

    if (!newErrors.date) {
      onValueChange({ date: newDate })
    }
  }

  const handleTimeChange = (newTime: string) => {
    setTime(newTime)

    const { newErrors } = validateAll({
      inRangeVendorLocations: availableVendorLocations,
      userState: { ...newUserState, time: newTime },
      useDeliveryTimes: cutOff5pm,
      deliveryTimes: tenant.features?.gmDeliveryTimeslots,
      t,
    })

    setErrors(newErrors)

    if (!newErrors.time) {
      onValueChange({ time: newTime })
    }
  }

  const handleHeadCountChange = (newHeadcount: number) => {
    setHeadcount(newHeadcount)

    const { newErrors } = validateAll({
      inRangeVendorLocations: availableVendorLocations,
      userState: { ...newUserState, headCount: newHeadcount },
      useDeliveryTimes: cutOff5pm,
      deliveryTimes: tenant.features?.gmDeliveryTimeslots,
      t,
    })

    setErrors(newErrors)

    if (!newErrors.time) {
      onValueChange({ headCount: newHeadcount })
    }
  }

  const handleConfirmModal = () => {
    if (!orderCanBeFulfilled) return
    onConfirm(basketValidationResult.value)
  }

  const openModal = () => {
    if (disabled) return
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
  }

  return (
    <>
      {showBasketEmbeddedVersion ? (
        <Box paddingX={2}>
          <ButtonBase
            disabled={disabled}
            onClick={openModal}
            sx={{
              borderRadius: 1,
              borderStyle: 'solid',
              borderWidth: '1px',
              borderColor: 'action.disabled',
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              ':hover': { borderColor: 'action.active' },
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                padding={1}
                sx={{
                  borderWidth: '1px',
                  borderColor: 'action.disabled',
                  borderBottomStyle: 'solid',
                }}
              >
                <Stack width="100%" direction="row" justifyContent="space-between" spacing={1}>
                  <Stack direction="row" spacing={1} width="100%">
                    <LocationOn fontSize="small" color="primary" />

                    {!loading ? (
                      <Typography
                        textAlign="left"
                        variant="body2"
                        color={pinAddress ? 'text.main' : 'text.disabled'}
                      >
                        {pinAddress || t('marketplace:enterAnAddress')}
                      </Typography>
                    ) : (
                      <Skeleton sx={{ width: '100%' }} />
                    )}
                  </Stack>

                  {!disabled && !loading ? <Edit fontSize="small" color="primary" /> : null}
                </Stack>
              </Grid>

              {orderSelectedSettings.weekdays && orderSelectedSettings.weekdays?.length > 0 ? (
                <Grid
                  item
                  padding={1}
                  xs={12}
                  sx={{
                    borderWidth: '1px',
                    borderColor: 'action.disabled',
                    borderBottomStyle: 'solid',
                  }}
                >
                  <Stack direction="row" spacing={0.5}>
                    {orderSelectedSettings.weekdays?.map((weekday) => (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        label={localeDate(setDay(new Date(), weekday), 'EEEEEE', locale)}
                        key={weekday}
                      />
                    ))}
                  </Stack>
                </Grid>
              ) : null}

              {!hideFields?.date ? (
                <Grid
                  item
                  padding={1}
                  xs={12}
                  sx={{
                    borderWidth: '1px',
                    borderColor: 'action.disabled',
                    borderBottomStyle: 'solid',
                  }}
                >
                  <Stack width="100%" direction="row" spacing={1}>
                    <CalendarToday fontSize="small" color="primary" />

                    {!loading ? (
                      <Typography
                        variant="body2"
                        color={orderSelectedSettings.date ? 'text.main' : 'text.disabled'}
                      >
                        {orderSelectedSettings.date
                          ? localeDate(orderSelectedSettings.date, 'do MMM yyyy', locale)
                          : t('marketplace:deliveryDate')}
                      </Typography>
                    ) : (
                      <Skeleton width={150} />
                    )}
                  </Stack>
                </Grid>
              ) : null}

              <Grid
                item
                padding={1}
                xs={8}
                sx={{
                  borderWidth: '1px',
                  borderColor: 'action.disabled',
                  borderRightStyle: 'solid',
                }}
              >
                <Stack direction="row" spacing={1}>
                  <AccessTime fontSize="small" color="primary" />
                  {!loading ? (
                    <Typography
                      variant="body2"
                      color={deliveryTimeLabel ? 'text.main' : 'text.disabled'}
                    >
                      {deliveryTimeLabel || t('marketplace:time')}
                    </Typography>
                  ) : (
                    <Skeleton width={110} />
                  )}
                </Stack>
              </Grid>

              <Grid padding={1} item xs={4}>
                <Stack direction="row" spacing={1}>
                  <Person fontSize="small" color="primary" />
                  {!loading ? (
                    <Typography variant="body2" color={pinAddress ? 'text.main' : 'text.disabled'}>
                      {orderSelectedSettings.headCount || '0'}
                    </Typography>
                  ) : (
                    <Skeleton width={30} />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </ButtonBase>
        </Box>
      ) : (
        <Stack
          direction="row"
          useFlexGap
          flexWrap="wrap"
          spacing={1}
          alignItems="center"
          id="input-bar"
        >
          <Typography width={{ xs: '100%', lg: 'fit-content' }} color="text.secondary">
            {t('marketplace:orderingFor')}
          </Typography>

          {orderSelectedSettings.weekdays && orderSelectedSettings.weekdays?.length > 0 ? (
            <Stack direction="row" spacing={0.5}>
              {orderSelectedSettings.weekdays?.map((weekday) => (
                <Chip
                  variant="outlined"
                  color="primary"
                  label={localeDate(setDay(new Date(), weekday), 'EEEEEE', locale)}
                  key={weekday}
                />
              ))}
            </Stack>
          ) : null}

          <TextField
            sx={{
              width: {
                xs: '100%',
                xl: showFullAddress ? '30%' : '200px',
                md: showFullAddress ? '28%' : '200px',
                sm: showFullAddress ? '23%' : '170px',
              },
              flexGrow: { xs: 1, md: 'unset' },
            }}
            onClick={openModal}
            size="small"
            placeholder={t('marketplace:deliverTo')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOn color="primary" />
                </InputAdornment>
              ),
            }}
            disabled={disabled}
            value={pinAddress}
          />

          {!isMobileScreen ? (
            <>
              {!hideFields?.date ? (
                <TextField
                  sx={{ width: { xs: '150px', md: '170px' }, flexGrow: { xs: 1, md: 'unset' } }}
                  onClick={openModal}
                  size="small"
                  placeholder={t('marketplace:date')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarToday color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  disabled={disabled}
                  value={localeDate(orderSelectedSettings.date || undefined, 'dd/MM/yyyy', locale)}
                />
              ) : null}

              <TextField
                sx={{ width: '175px' }}
                onClick={openModal}
                size="small"
                placeholder={t('marketplace:time')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTime color="primary" />
                    </InputAdornment>
                  ),
                }}
                disabled={disabled}
                value={deliveryTimeLabel}
              />

              <TextField
                sx={{ width: '110px' }}
                onClick={openModal}
                size="small"
                placeholder="0"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="primary" />
                    </InputAdornment>
                  ),
                }}
                disabled={disabled}
                value={orderSelectedSettings.time ? orderSelectedSettings.headCount : ''}
              />

              <Stack
                flexWrap="wrap"
                flexGrow={1}
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
              >
                {tenant.features?.cateringAllergenTooltip?.visibility !== false && (
                  <MuiTooltip title={allergenInfoText}>
                    <Button size="small" variant="text" startIcon={<InfoRounded />}>
                      {t('marketplace:allergens')}
                    </Button>
                  </MuiTooltip>
                )}
              </Stack>
            </>
          ) : null}
        </Stack>
      )}

      <Dialog open={modalOpen} fullWidth onClose={closeModal} fullScreen={isMobileScreen}>
        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
          <Box textAlign="center" p={2}>
            <Typography variant="h4" component="span">
              {t('marketplace:confirmYourDetails')}
            </Typography>

            <Typography color="text.secondary">{t('marketplace:wellMakeSureYour')}</Typography>
          </Box>

          {isMobileScreen && (
            <Box>
              <IconButton onClick={closeModal}>
                <Close />
              </IconButton>
            </Box>
          )}
        </Stack>

        <DialogContent
          sx={{
            px: 3,
            py: 4,
          }}
        >
          <AddressLookup
            error={loadingInRangeLocationData ? '' : errors.address}
            onAddressChanged={(newLocation) => {
              handleAddressChange(newLocation)
            }}
            selectedLocation={location}
            availableAccountLocations={
              authState.type === 'authenticated' ? (authState.user.account?.locations ?? []) : []
            }
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <DatepickerInput
                error={errors.date}
                showErrors={Boolean(errors.date && !errors.headCount)}
                disabled={!location}
                availableVendorLocations={availableVendorLocations}
                dateValue={date}
                onChange={(newDate) => {
                  handleDateChange(newDate)
                }}
                forceClose={closeCalendar}
                showChevron={true}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TimeSelect
                error={errors.time}
                onTimeChange={(newTime) => {
                  handleTimeChange(newTime)
                }}
                selectedDate={date}
                selectedTime={time}
                availableVendorLocations={inRangeVendorLocations}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <HeadCountInput
                onChange={(newHeadcount) => {
                  handleHeadCountChange(newHeadcount)
                }}
                headCount={headcount}
                error={time && date ? errors.headCount : ''}
                maxHeadCount={
                  availableVendorLocations.reduce(
                    (acc, location) => Math.max(location.details.gmMaxOrder || 0, acc),
                    0,
                  ) || Number.POSITIVE_INFINITY
                }
                unusable={!time}
                disabled={!time}
              />
            </Grid>
          </Grid>

          <ButtonWrapper>
            <LoadingButton
              variant="contained"
              disabled={!orderCanBeFulfilled}
              fullWidth
              loading={modalIsLoading}
              onClick={handleConfirmModal}
            >
              {t('marketplace:confirm')}
            </LoadingButton>
          </ButtonWrapper>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default InputBar
